import React, { useState } from "react";
import NewPartnerFields, { NEW_PARTNER_REASON, emptyNewPartner, isNewPartnerComplete, needsSituationChoice } from "./NewPartnerFields";
import { FiChevronRight, FiEdit3, FiPlus, FiUser, FiUsers } from "react-icons/fi";
import AppCard from "./components/AppCard";
import FamilySectionTitle from "./components/FamilySectionTitle";
import PersonRow from "./components/PersonRow";
import { getChildAge, getChildKey, getChildName, getFullName, getBirthDateValue, formatDate } from "./utils/childUtils";
import { getClassroomLabel } from "./utils/classroomUtils";
import { getEnabledPrivacyCount } from "./utils/privacyUtils";

const ChevronRightIcon = FiChevronRight;
const EditIcon = FiEdit3;
const PlusIcon = FiPlus;
const UserIcon = FiUser;
const UsersIcon = FiUsers;

const FAMILY_SITUATIONS = [
  "MARRIED",
  "COHABITING",
  "SEPARATED",
  "DIVORCED",
  "SINGLE_PARENT",
  "WIDOWED",
  "OTHER",
];

const FAMILY_SITUATION_DEFAULTS = {
  MARRIED: "Married",
  COHABITING: "Cohabiting",
  SEPARATED: "Separated",
  DIVORCED: "Divorced",
  SINGLE_PARENT: "Single parent",
  WIDOWED: "Widowed",
  OTHER: "Other",
};

const familySituationLabel = (t, value) =>
  t(`parentPortal.familySituation.options.${value || "MARRIED"}`, {
    defaultValue: FAMILY_SITUATION_DEFAULTS[value] || FAMILY_SITUATION_DEFAULTS.MARRIED,
  });

const FAMILY_SITUATION_REASONS = [
  { value: "NEW_PARTNER", label: "Nieuwe partner" },
  { value: "SINGLE_PARENT", label: "Alleenstaande ouder" },
  { value: "CHILD_OTHER_FAMILY", label: "Kind hoort bij ander gezin" },
  { value: "INCORRECT_LINK", label: "Onjuiste koppeling" },
  { value: "OTHER", label: "Anders" },
];

const FamilyOverview = ({
  t,
  family,
  familyDraft,
  setFamilyDraft,
  familyEditMode,
  setFamilyEditMode,
  hasPendingFamilyUpdate,
  hasPendingFamilySituationRequest,
  handleSaveFamilySituation,
  onSubmitFamilySituationRequest,
  parentData,
  onOpenParent,
  onSelectChild,
  onAddChild,
  classroomsByChild,
  privacyByChild,
}) => {
  const [situationFormOpen, setSituationFormOpen] = useState(false);
  const [situationReason, setSituationReason] = useState("NEW_PARTNER");
  const [situationNote, setSituationNote] = useState("");
  const [situationSaving, setSituationSaving] = useState(false);
  const [situationSubmitted, setSituationSubmitted] = useState(false);
  const [situationError, setSituationError] = useState("");
  const [newPartner, setNewPartner] = useState(emptyNewPartner);
  const reportsNewPartner = situationReason === NEW_PARTNER_REASON;

  const parent1 = family?.parent1Id
    ? {
        id: family.parent1Id,
        firstName: family.parent1FirstName,
        lastName: family.parent1LastName,
        role: t("parentPortal.parent_1", { defaultValue: "Parent 1" }),
      }
    : null;

  const parent2 = family?.parent2Id
    ? {
        id: family.parent2Id,
        firstName: family.parent2FirstName,
        lastName: family.parent2LastName,
        role: t("parentPortal.parent_2", { defaultValue: "Parent 2" }),
      }
    : null;

  const parents = [parent1, parent2].filter(Boolean);
  const children = Array.isArray(family?.children) ? family.children : [];
  const familyName =
    family?.familyName ||
    getFullName(parentData)?.split(" ").slice(-1).join(" ") ||
    t("parentPortal.family", { defaultValue: "Family" });

  const familySituationValue = familyEditMode
    ? familyDraft?.familySituation || "MARRIED"
    : familyDraft?.familySituation || family?.familySituation || "MARRIED";

  const situationPending = hasPendingFamilySituationRequest || situationSubmitted;

  const submitSituationRequest = async (event) => {
    event.preventDefault();
    if (situationSaving || situationPending) return;

    setSituationSaving(true);
    setSituationError("");

    if (reportsNewPartner && !isNewPartnerComplete(newPartner)) {
      setSituationSaving(false);
      setSituationError(t("parentPortal.newPartner.incomplete", {
        defaultValue: "Fill in the name and e-mail address of your partner.",
      }));
      return;
    }
    if (reportsNewPartner && needsSituationChoice(family?.familySituation || "MARRIED") && !newPartner.familySituation) {
      setSituationSaving(false);
      setSituationError(t("parentPortal.newPartner.situationRequired", {
        defaultValue: "Choose the situation of your current family.",
      }));
      return;
    }

    const ok = await onSubmitFamilySituationRequest?.({
      reason: situationReason,
      note: situationNote.trim(),
      partner: reportsNewPartner ? newPartner : undefined,
      currentSituation: family?.familySituation || "MARRIED",
    });

    setSituationSaving(false);

    if (ok) {
      setSituationSubmitted(true);
      setSituationFormOpen(false);
      setSituationNote("");
      setNewPartner(emptyNewPartner());
      return;
    }

    setSituationError(
      t("parentPortal.request.messages.error", {
        defaultValue: "Request failed. Please try again.",
      }),
    );
  };

  return (
    <div className="pp-family-view mx-auto grid w-full max-w-[33rem] gap-4 pb-2">
      <AppCard className="px-5 py-5">
        {family ? (
          <>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
              <div className="min-w-0">
                <h2 className="m-0 text-[1.28rem] font-black leading-tight text-slate-950 dark:text-slate-50">
                  {t("parentPortal.my_family", { defaultValue: "My family" })}
                </h2>
                <p className="mt-1 truncate text-[1.08rem] font-semibold leading-tight text-slate-500 dark:text-slate-400">
                  {familyName}
                </p>
                <p className="mt-2 text-[0.92rem] font-semibold leading-tight text-slate-500 dark:text-slate-400">
                  {t("parentPortal.parents_count", {
                    defaultValue: "{{count}} parents",
                    count: parents.length,
                  })}
                  ,{" "}
                  {t("parentPortal.children_count", {
                    defaultValue: "{{count}} children",
                    count: children.length,
                  })}
                </p>
              </div>

              {!familyEditMode && (
                <button
                  type="button"
                  onClick={() => setFamilyEditMode(true)}
                  className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-emerald-50 px-4 text-[0.88rem] font-black text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                >
                  <EditIcon className="h-4 w-4" aria-hidden="true" />
                  {t("common.actions.edit", { defaultValue: "Edit" })}
                </button>
              )}
            </div>

            <div className="mt-5">
              <PersonRow
                icon={<UsersIcon className="h-6 w-6" />}
                title={t("parentPortal.family_situation", { defaultValue: "Family situation" })}
                subtitle={
                  familyEditMode
                    ? undefined
                    : familySituationLabel(t, familySituationValue)
                }
              />

              {familyEditMode && (
                <div className="ml-[4rem] mt-2 grid gap-2">
                  <select
                    className="min-h-11 rounded-2xl border border-slate-200 bg-white/85 px-3 text-[0.92rem] font-bold text-slate-900 outline-none disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100"
                    value={familySituationValue}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        familySituation: event.target.value,
                      }))
                    }
                    disabled={hasPendingFamilyUpdate}
                  >
                    {FAMILY_SITUATIONS.map((value) => (
                      <option key={value} value={value}>
                        {familySituationLabel(t, value)}
                      </option>
                    ))}
                  </select>

                  {hasPendingFamilyUpdate && (
                    <span className="inline-flex w-fit items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-800">
                      {t("parentPortal.pending_change", { defaultValue: "Change requested" })}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="my-5 h-px bg-slate-200/80" />

            <h3 className="mb-2 text-[0.92rem] font-black text-slate-950 dark:text-slate-50">
              {t("parentPortal.parents", { defaultValue: "Parents" })}
            </h3>

            <div className="grid gap-1">
              {parents.length > 0 ? (
                parents.map((parent) => (
                  <PersonRow
                    key={parent.id}
                    icon={<UserIcon className="h-6 w-6" />}
                    title={getFullName(parent) || t("parentPortal.parent", { defaultValue: "Parent" })}
                    subtitle={parent.role}
                    onClick={typeof onOpenParent === "function" ? () => onOpenParent(parent) : undefined}
                  />
                ))
              ) : (
                <p className="m-0 py-2 text-[0.9rem] font-semibold text-slate-500 dark:text-slate-400">
                  {t("parentPortal.no_partner", { defaultValue: "No partner" })}
                </p>
              )}
            </div>

            {familyEditMode && (
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFamilyDraft(family);
                    setFamilyEditMode(false);
                  }}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-200 bg-white/70 px-4 text-[0.9rem] font-black text-slate-700 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200"
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  onClick={handleSaveFamilySituation}
                  disabled={hasPendingFamilyUpdate}
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-emerald-600 px-4 text-[0.9rem] font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("common.save", { defaultValue: "Save" })}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="m-0 text-[0.95rem] font-semibold text-slate-500 dark:text-slate-400">
            {t("parentPortal.no_family_found", { defaultValue: "No family found" })}
          </p>
        )}
      </AppCard>

      {family && (
        <section className="rounded-[1.45rem] border border-slate-200/80 bg-white px-4 py-4 shadow-[0_0.65rem_1.45rem_rgba(15,23,42,0.06)] dark:border-slate-700/80 dark:bg-slate-900/80 dark:shadow-black/20">
          <div className="grid gap-2">
            <h3 className="m-0 text-[1rem] font-black leading-tight text-slate-950 dark:text-slate-50">
              Gezinssituatie klopt niet?
            </h3>
            <p className="m-0 text-[0.86rem] font-semibold leading-snug text-slate-500 dark:text-slate-400">
              Bijvoorbeeld bij een nieuwe partner, alleenstaand ouderschap of een onjuiste koppeling.
            </p>

            {situationPending ? (
              <span className="mt-1 inline-flex w-fit items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200">
                Je verzoek is in behandeling.
              </span>
            ) : situationFormOpen ? (
              <form className="mt-2 grid gap-3" onSubmit={submitSituationRequest}>
                <label className="grid gap-1 text-[0.82rem] font-black text-slate-950 dark:text-slate-100">
                  Reden
                  <select
                    value={situationReason}
                    onChange={(event) => setSituationReason(event.target.value)}
                    className="min-h-11 rounded-2xl border border-slate-200 bg-white px-3 text-[0.92rem] font-bold text-slate-900 outline-none dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-100"
                  >
                    {FAMILY_SITUATION_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-1 text-[0.82rem] font-black text-slate-950 dark:text-slate-100">
                  Toelichting
                  <textarea
                    rows={4}
                    required={!reportsNewPartner}
                    value={situationNote}
                    onChange={(event) => setSituationNote(event.target.value)}
                    className="min-h-[6.5rem] rounded-2xl border border-slate-200 bg-white px-3 py-3 text-[0.92rem] font-semibold text-slate-900 outline-none dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-100"
                  />
                </label>

                {reportsNewPartner && (
                  <NewPartnerFields
                    t={t}
                    value={newPartner}
                    onChange={setNewPartner}
                    currentSituation={family?.familySituation || "MARRIED"}
                    situationOptions={FAMILY_SITUATIONS.map((value) => ({ value, label: familySituationLabel(t, value) }))}
                    inputClassName="min-h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-[0.92rem] font-semibold text-slate-900 outline-none dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-100"
                    labelClassName="grid gap-1 text-[0.82rem] font-black text-slate-950 dark:text-slate-100"
                  />
                )}

                {situationError && (
                  <p className="m-0 text-[0.82rem] font-semibold text-red-700">
                    {situationError}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSituationFormOpen(false)}
                    className="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-4 text-[0.88rem] font-black text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {t("common.cancel", { defaultValue: "Cancel" })}
                  </button>
                  <button
                    type="submit"
                    disabled={situationSaving}
                    className="inline-flex min-h-11 items-center justify-center rounded-full bg-emerald-600 px-4 text-[0.88rem] font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {situationSaving
                      ? t("common.saving", { defaultValue: "Saving..." })
                      : "Contact opnemen"}
                  </button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSituationFormOpen(true)}
                className="mt-1 inline-flex min-h-11 w-fit items-center justify-center rounded-full bg-emerald-600 px-4 text-[0.88rem] font-black text-white"
              >
                Contact opnemen
              </button>
            )}
          </div>
        </section>
      )}

      <FamilySectionTitle>
        {t("parentPortal.children.children", { defaultValue: "Children" })}
      </FamilySectionTitle>

      <AppCard className="overflow-hidden px-3 py-3">
        {children.length > 0 ? (
          <div className="grid gap-2">
            {children.map((child) => {
              const childName = getChildName(child, t);
              const age = getChildAge(child);
              const key = getChildKey(child);
              const privacyState = privacyByChild?.[key];
              const privacyCount = getEnabledPrivacyCount(privacyState?.data);

              return (
                <PersonRow
                  key={child.id || child.uuid}
                  icon={<UserIcon className="h-6 w-6" />}
                  tone="pink"
                  title={age !== null ? `${childName} (${age})` : childName}
                  subtitle={
                    formatDate(getBirthDateValue(child)) ||
                    getClassroomLabel(child, classroomsByChild, t)
                  }
                  badge={
                    privacyState?.data ? (
                      <span className="mt-1 block text-[0.72rem] font-black text-emerald-700">
                        {t("parentPortal.children.privacy_count", {
                          defaultValue: "{{count}} privacy preference(s)",
                          count: privacyCount,
                        })}
                      </span>
                    ) : null
                  }
                  onClick={() => onSelectChild(child)}
                />
              );
            })}
          </div>
        ) : (
          <p className="m-0 py-4 text-[0.95rem] font-semibold text-slate-500 dark:text-slate-400">
            {t("parentPortal.no_children", { defaultValue: "No children" })}
          </p>
        )}
      </AppCard>

      <button
        type="button"
        onClick={typeof onAddChild === "function" ? onAddChild : undefined}
        className="grid min-h-[5.65rem] w-full grid-cols-[3.55rem_minmax(0,1fr)_auto] items-center gap-4 rounded-[1.45rem] border border-white/70 bg-emerald-50/65 px-4 py-4 text-left shadow-[0_0.75rem_1.6rem_rgba(15,23,42,0.07)] backdrop-blur dark:border-emerald-900/50 dark:bg-emerald-950/35 dark:shadow-black/20"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200">
          <PlusIcon className="h-6 w-6" />
        </span>
        <span className="min-w-0">
          <span className="block text-[0.98rem] font-black leading-tight text-slate-950 dark:text-slate-50">
            {t("parentPortal.children.add_child", { defaultValue: "Add child" })}
          </span>
          <span className="mt-1 block text-[0.84rem] font-semibold leading-snug text-slate-500 dark:text-slate-400">
            {t("parentPortal.children.add_child_description", {
              defaultValue: "Add a child to your family composition.",
            })}
          </span>
        </span>
        <ChevronRightIcon className="h-6 w-6 text-emerald-600" />
      </button>
    </div>
  );
};

export default FamilyOverview;
